import {
  GetSubscriptionResponse,
  GetUserProfileResponse,
  PlanType as OtterPlanType,
  Plan,
} from '@otter-ai/recall-client';
import {
  BillingCycle,
  BillingInterval,
  CalendarType,
  CheckoutEntryPoint,
  EmailDomainType,
  ingestDebugTraceEvent,
  PlanType,
  PremiumFeature,
  PurchaseAttemptEvent,
  PurchaseSuccessEvent,
  SignupCohort,
  TEventContext,
  trackEvent,
} from '@otter-ai/telemetry';
import { StripeCheckoutSession } from '@stripe/stripe-js';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { couponInfoAtom, planTypeAtom, selectedPlanAtom } from '#atoms';
import { MINUTE_MS } from '#constants';
import { useEmailMeta, useTrackEventWithGeoLocation } from '#hooks';
import {
  loginInfoQueryCreator,
  userLocationQueryCreator,
  userProfileQueryCreator,
} from '#query';
import { getFromLocalStorage, removeFromLocalStorage } from '#utils';

export const PLAN_UPGRADE_SRC_EL = 'PLAN_UPGRADE_SRC_EL';

export const AmplitudeEventType = {
  Purchase_FailureEvent: 'Purchase_FailureEvent',
} as const;

// Union type: either full session or just the required properties
export type SessionInput =
  | StripeCheckoutSession
  | Pick<
      StripeCheckoutSession,
      | 'total'
      | 'minorUnitsAmountDivisor'
      | 'lineItems'
      | 'discountAmounts'
      | 'recurring'
      | 'shippingAddress'
    >;

type UserInput = Partial<GetUserProfileResponse['user']>;

export function usePurchaseAnalytics({
  planTypeParam,
  selectedPlanParam,
}: {
  planTypeParam?: OtterPlanType;
  selectedPlanParam?: Plan;
} = {}) {
  const { trackEventWithGeoIP } = useTrackEventWithGeoLocation();
  const { data: loginData } = useQuery(loginInfoQueryCreator());
  const { data: userGeoipLocation } = useQuery(userLocationQueryCreator());
  const { data: profileData } = useQuery(
    userProfileQueryCreator({ enabled: loginData?.['logged-in'] }),
  );

  const userEmail = profileData?.email;
  const checkEmailEnabled = !!userEmail && loginData?.['logged-in'];
  const { emailMeta } = useEmailMeta({
    email: userEmail,
    enabled: checkEmailEnabled,
  });

  const planTypeAtomValue = useAtomValue(planTypeAtom);
  const couponInfo = useAtomValue(couponInfoAtom);
  const selectedPlanAtomValue = useAtomValue(selectedPlanAtom);

  const planType = planTypeParam ?? planTypeAtomValue;
  const selectedPlan = selectedPlanParam ?? selectedPlanAtomValue;

  /** gets calendar connection info from latestProfileData */
  function getCalendarProperties({
    userData,
  }: {
    userData: UserInput | undefined;
  }): {
    CalendarConnection: boolean;
    CalendarTypes: CalendarType[];
  } {
    const userDataToUse = userData ?? profileData?.user;
    if (!userDataToUse?.synced_accounts) {
      return { CalendarConnection: false, CalendarTypes: [] };
    }

    const syncedAccounts = userDataToUse?.synced_accounts as Array<{
      source: string;
      permission?: string[];
    }>;

    const calendarSources = syncedAccounts
      // keep only accounts that have calendar permission
      .filter((acc) => acc.permission?.includes('calendar'))
      // pull out the source name
      .map((acc) => acc.source as CalendarType);

    const CalendarTypes = Array.from(new Set(calendarSources));
    const CalendarConnection = CalendarTypes.length > 0;
    return { CalendarConnection, CalendarTypes };
  }

  /** determines if user should be considered "new" for analytics - signed up in the last 10 minutes */
  function getIsNewUser({
    userData,
  }: {
    userData?: UserInput | undefined;
  }): boolean {
    const userDataToUse = userData ?? profileData?.user;
    if (userDataToUse?.date_joined) {
      const joinedDate = new Date(userDataToUse.date_joined);
      const now = new Date();
      const minutesSinceJoin =
        (now.getTime() - joinedDate.getTime()) / MINUTE_MS;
      if (minutesSinceJoin < 10) {
        return true;
      }
    }
    return false;
  }

  /** generates tracking properties for a subscription-related purchase event */
  function getSubscriptionPurchaseAnalyticsProperties({
    salesTaxCharged,
    subscription,
    userData,
  }: {
    salesTaxCharged: number;
    subscription: GetSubscriptionResponse['subscription'];
    userData?: UserInput | undefined;
  }) {
    try {
      // TODO: double check the subscription id logic
      const subscriptionId =
        planType === 'premium'
          ? subscription?.subscription_id
          : subscription.team_subscription_id;
      const userDataToUse = userData ?? profileData?.user;
      const transactionId = `${userDataToUse?.id}_${subscriptionId}`;

      const userIsInTrial =
        subscription?.coupon_trial_end_at !== null ||
        subscription?.trial_end_at !== null;

      return {
        TransactionID: transactionId,
        SalesTaxAmount: salesTaxCharged,
        IsTrial: userIsInTrial,
      };
    } catch (err) {
      ingestDebugTraceEvent('getSubscriptionPurchaseAnalyticsProperties', {
        error_message: err instanceof Error ? err.message : String(err),
      });
      return {};
    }
  }

  /** generates tracking properties for a typical purchase event */
  function getCommonPurchaseAnalyticsProperties({
    appliedCouponCode,
    seatCount,
    subtotal,
    revenue,
    orderDiscount,
  }: {
    appliedCouponCode: string | undefined;
    seatCount: number;
    subtotal: number;
    revenue: number;
    orderDiscount: number;
  }) {
    try {
      return {
        Revenue: String(revenue), // what they paid (total - discounts - applied credits), not including tax
        OrderDiscount: orderDiscount,
        ProductItems: [
          {
            ProductItemID: String(selectedPlan?.id),
            ProductItemName: selectedPlan?.name,
            ProductItemDisplayName: selectedPlan?.display_name ?? undefined,
            ProductItemPrice: Number(selectedPlan?.price),
            ProductItemSubtotal: subtotal, // total price of the session (plan price * seats), excluding tax and discounts
            SeatCount: seatCount,
            PlanType: selectedPlan?.type as PlanType,
            BillingInterval:
              selectedPlan?.payment_period_type as BillingInterval,
            CouponCode: appliedCouponCode,
          },
        ],
      };
    } catch (err) {
      ingestDebugTraceEvent('getCommonPurchaseAnalyticsProperties', {
        error_message: err instanceof Error ? err.message : String(err),
      });
      return {};
    }
  }

  function getPurchaseAnalytics({
    salesTaxAmount,
    usedSavedPayment,
    appliedCouponCode,
    revenue,
    total,
    couponType,
    userCountry,
    userZip,
    percentOff,
    isPaymentRequired,
    couponPeriodAmount,
    isBasicWorkspace,
    isBasicAdmin,
    isBasicMember,
    userData,
    couponAmountDiscount,
    emailDomainType,
    checkoutType,
  }: {
    salesTaxAmount: number;
    usedSavedPayment?: boolean;
    appliedCouponCode: string | undefined;
    revenue: number;
    total: number;
    couponType: 'percent' | 'cycle' | undefined;
    userCountry: string | undefined;
    userZip: string | undefined;
    percentOff: number | undefined;
    isPaymentRequired: boolean;
    couponPeriodAmount?: number;
    isBasicWorkspace: boolean;
    isBasicAdmin: boolean;
    isBasicMember: boolean;
    userData?: UserInput | undefined;
    couponAmountDiscount: number;
    emailDomainType?: EmailDomainType | null;
    checkoutType?: string;
  }) {
    // Get the element they upgraded from, and then remove it from localStorage
    const planUpgradeSrcEl = getFromLocalStorage<string>(PLAN_UPGRADE_SRC_EL);
    removeFromLocalStorage(PLAN_UPGRADE_SRC_EL);

    return {
      PlanType: selectedPlan?.name as PlanType,
      CouponCode: appliedCouponCode,
      Revenue: String(revenue), // what they paid (total - discounts - applied credits), not including tax
      PriceCharged: total, // what the user paid, including tax and discounts

      CouponType: couponType,
      CouponPercentDiscount: percentOff,
      CouponAmountDiscount: couponAmountDiscount, // the dollar amount of the discount, if it existed
      CouponPaymentRequired: isPaymentRequired,
      CouponPeriodAmount: couponPeriodAmount,

      SavedPayment: usedSavedPayment ?? false,
      PlanUpgradeSrcEl: planUpgradeSrcEl,

      BasicWorkspace: isBasicWorkspace,
      isNewUser: getIsNewUser({ userData }),
      BasicWorkspaceAdmin: isBasicAdmin,
      BasicWorkspaceMember: isBasicMember,
      EmailDomainType: emailDomainType ?? emailMeta?.domainType ?? undefined, // use the override from args if available (for new user)

      ...getCalendarProperties({ userData }),

      SalesTaxAmount: salesTaxAmount,
      LocationInformationFilled: Boolean(userCountry) && Boolean(userZip),

      BillingCycle:
        selectedPlan?.payment_period_type === 'year'
          ? BillingCycle.annually
          : selectedPlan?.payment_period_type === 'month'
            ? BillingCycle.monthly
            : undefined,
      BillingInterval:
        selectedPlan?.payment_period_type === 'year'
          ? BillingInterval.annual
          : selectedPlan?.payment_period_type === 'month'
            ? BillingInterval.monthly
            : undefined,
      CheckoutType: checkoutType,
    };
  }

  /** generates tracking context for analytics events */
  function getPurchaseAnalyticsContext({
    userCountry,
    userData,
  }: {
    userCountry: string | undefined;
    userData?: UserInput | undefined;
  }): TEventContext {
    try {
      const userDataToUse = userData ?? profileData?.user;
      return {
        user: {
          country: userCountry,
          ...userDataToUse,
          country_api: userGeoipLocation?.country_code,
        },
      };
    } catch (err) {
      ingestDebugTraceEvent('getPurchaseAnalyticsContext', {
        error_message: err instanceof Error ? err.message : String(err),
      });
      return {};
    }
  }

  function logPurchaseSuccessEvent({
    session,
    userData,
    usedSavedPayment,
    isPaymentRequired,
    subscription,
    isBasicWorkspace,
    isBasicAdmin,
    isBasicMember,
    emailDomainType,
    checkoutType,
    premiumFeature,
    checkoutEntryPoint,
  }: {
    session: SessionInput;
    userData?: UserInput | undefined;
    usedSavedPayment?: boolean;
    isPaymentRequired: boolean;
    subscription: GetSubscriptionResponse['subscription'];
    isBasicWorkspace: boolean;
    isBasicAdmin: boolean;
    isBasicMember: boolean;
    emailDomainType?: EmailDomainType | null;
    checkoutType?: string;
    premiumFeature?: PremiumFeature | null;
    checkoutEntryPoint?: CheckoutEntryPoint | null;
  }) {
    try {
      const salesTaxCharged =
        session.total.taxExclusive.minorUnitsAmount /
        session.minorUnitsAmountDivisor;
      const seatCount = session.lineItems[0]?.quantity ?? 0;

      const percentOffCouponAppliedFinal = Boolean(
        session.discountAmounts?.length,
      );
      const cycleOffCouponAppliedFinal = Boolean(
        session.recurring?.trial?.trialPeriodDays,
      );
      const isCouponInSessionFinal =
        percentOffCouponAppliedFinal || cycleOffCouponAppliedFinal;
      const appliedCouponCode = isCouponInSessionFinal
        ? (couponInfo?.code ?? undefined)
        : undefined;

      const subtotal =
        session.total.subtotal.minorUnitsAmount /
        session.minorUnitsAmountDivisor;
      const revenue =
        session.total.total.minorUnitsAmount / session.minorUnitsAmountDivisor -
        salesTaxCharged;

      const couponAmountDiscount =
        (session.discountAmounts?.[0]?.minorUnitsAmount ?? 0) /
        session.minorUnitsAmountDivisor;

      const orderDiscount =
        (session.total.appliedBalance?.minorUnitsAmount ?? 0) /
          session.minorUnitsAmountDivisor +
        couponAmountDiscount;

      const total =
        session.total.total.minorUnitsAmount / session.minorUnitsAmountDivisor;

      const userCountry = session.shippingAddress?.address.country;
      const userZip = session.shippingAddress?.address.postal_code ?? undefined;

      const purchaseTracking = {
        ...getSubscriptionPurchaseAnalyticsProperties({
          salesTaxCharged,
          subscription,
          userData,
        }),
        ...getCommonPurchaseAnalyticsProperties({
          appliedCouponCode,
          seatCount,
          subtotal,
          revenue,
          orderDiscount,
        }),
        ...{
          SeatCount: seatCount,
          SignupCohort: 'a' as SignupCohort,
        },
        ...getPurchaseAnalytics({
          salesTaxAmount: salesTaxCharged,
          usedSavedPayment,
          appliedCouponCode,
          revenue,
          total,
          couponType: percentOffCouponAppliedFinal
            ? 'percent'
            : cycleOffCouponAppliedFinal
              ? 'cycle'
              : undefined,
          userData,
          userCountry,
          userZip,
          percentOff: percentOffCouponAppliedFinal
            ? (session.discountAmounts?.[0]?.percentOff ?? undefined)
            : undefined,
          isPaymentRequired,
          couponPeriodAmount: session.recurring?.trial?.trialPeriodDays,
          isBasicWorkspace,
          isBasicAdmin,
          isBasicMember,
          couponAmountDiscount,
          emailDomainType,
          checkoutType,
        }),
        ...(premiumFeature && { PremiumFeature: premiumFeature }),
        ...(checkoutEntryPoint && { CheckoutEntryPoint: checkoutEntryPoint }),
      };
      trackEventWithGeoIP(
        PurchaseSuccessEvent(purchaseTracking),
        getPurchaseAnalyticsContext({ userCountry, userData }),
      );
    } catch (err) {
      ingestDebugTraceEvent('logPurchaseSuccessEvent', {
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  function logPurchaseAttemptEvent({ session }: { session: SessionInput }) {
    try {
      const percentOffCouponAppliedFinal = Boolean(
        session.discountAmounts?.length,
      );
      const cycleOffCouponAppliedFinal = Boolean(
        session.recurring?.trial?.trialPeriodDays,
      );
      const isCouponInSessionFinal =
        percentOffCouponAppliedFinal || cycleOffCouponAppliedFinal;
      const appliedCouponCode = isCouponInSessionFinal
        ? (couponInfo?.code ?? undefined)
        : undefined;

      const seatCount = session.lineItems[0]?.quantity ?? 0;
      const subtotal =
        session.total.subtotal.minorUnitsAmount /
        session.minorUnitsAmountDivisor;

      const salesTaxCharged =
        session.total.taxExclusive.minorUnitsAmount /
        session.minorUnitsAmountDivisor;
      const revenue =
        session.total.total.minorUnitsAmount / session.minorUnitsAmountDivisor -
        salesTaxCharged;
      const orderDiscount =
        session.total.appliedBalance?.minorUnitsAmount /
          session.minorUnitsAmountDivisor +
        (session.discountAmounts?.[0]?.minorUnitsAmount ?? 0) /
          session.minorUnitsAmountDivisor;

      const userCountry = session.shippingAddress?.address.country;
      return trackEvent(
        PurchaseAttemptEvent(
          getCommonPurchaseAnalyticsProperties({
            appliedCouponCode,
            seatCount,
            subtotal,
            revenue,
            orderDiscount,
          }),
        ),
        getPurchaseAnalyticsContext({ userCountry }),
      );
    } catch (err) {
      ingestDebugTraceEvent('logPurchaseAttemptEvent', {
        error_message: err instanceof Error ? err.message : String(err),
      });
    }
  }

  return {
    logPurchaseSuccessEvent,
    logPurchaseAttemptEvent,
  };
}
